import { Progress, ProgressFilledTrack } from '@/components/ui/progress'
import { Text } from '@/components/ui/text'
import { getUsageAndBalanceThunk } from '@/redux/slices/usageAndBalanceSlice'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { useEffect, useMemo } from 'react'
import { ActivityIndicator, Platform, ScrollView, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

type PlanItemType = {
  consumedQuota: number
  totalQuota: number
  progress: number
  type: string
  unit: string
  unlimited: boolean
  decimalPlaces?: number
}

const typeMap: { [key: string]: string } = {
  entryUsage: '数据',
  activeMailUsage: '邮件',
  smsUsage: '短信',
  storageUsage: '附件',
  entryTransactionUsage: '交易',
  aiPointsUsage: 'AI点数',
  audioQuotaUsage: '音频',
}

const unitMap: { [key: string]: string } = {
  entryUsage: '条',
  activeMailUsage: '封',
  smsUsage: '条',
  entryTransactionUsage: '元',
  storageUsage: 'MB',
  aiPointsUsage: '点',
  audioQuotaUsage: '分钟',
}

// Conversion factor applied to raw API values before display
const conversionMap: { [key: string]: number } = {
  audioQuotaUsage: 1 / 60, // API stores seconds, display as minutes
  entryTransactionUsage: 1 / 100, // API stores fen, display as yuan
}

const decimalPlacesMap: { [key: string]: number } = {
  entryTransactionUsage: 2,
}

const UNLIMITED_QUOTA = 10_000_000_000

export default function PlanQuota() {
  const insets = useSafeAreaInsets()
  const dispatch = useAppDispatch()
  const { loaded, currentMonthlyUsage } = useAppSelector(state => state.usageAndBalance)

  useEffect(() => {
    if (!loaded) {
      dispatch(getUsageAndBalanceThunk())
    }
  }, [dispatch, loaded])

  const items = useMemo(() => {
    if (!loaded || !currentMonthlyUsage) return []

    const result: PlanItemType[] = []
    const usageKeys = [
      'entryUsage',
      'activeMailUsage',
      'smsUsage',
      'storageUsage',
      'aiPointsUsage',
      'audioQuotaUsage',
      'entryTransactionUsage',
    ]

    usageKeys.forEach(key => {
      const usage = currentMonthlyUsage[key as keyof typeof currentMonthlyUsage]
      if (usage && typeof usage === 'object' && 'totalQuota' in usage && 'consumedQuota' in usage) {
        const factor = conversionMap[key] ?? 1
        const totalQuota = (usage.totalQuota ?? 0) * factor
        const consumedQuota = (usage.consumedQuota ?? 0) * factor
        const progress = totalQuota !== 0 ? consumedQuota / totalQuota : 0
        const unlimited = (usage.totalQuota ?? 0) === UNLIMITED_QUOTA
        result.push({
          consumedQuota,
          totalQuota,
          progress: Number(progress.toFixed(4)),
          type: typeMap[key] ?? '',
          unit: unitMap[key] ?? '',
          unlimited,
          decimalPlaces: decimalPlacesMap[key],
        })
      }
    })

    return result
  }, [loaded, currentMonthlyUsage])

  if (!loaded) {
    return (
      <View className="flex-1 items-center justify-center">
        <ActivityIndicator size="large" />
      </View>
    )
  }

  return (
    <ScrollView
      contentInsetAdjustmentBehavior="automatic"
      contentContainerStyle={Platform.OS === 'android' ? { paddingBottom: insets.bottom } : undefined}
    >
      <View className="px-6 pt-4">
        <View className="mb-6 ">
          <Text className="text-typography-500">下次重置时间：{currentMonthlyUsage.resetDate}</Text>
        </View>

        {items.map((item: PlanItemType, index: number) => {
          const remainingQuota = item.totalQuota - item.consumedQuota
          const progressPercentage = (1 - item.progress) * 100

          const fmt = (n: number) =>
            item.decimalPlaces !== undefined
              ? n.toLocaleString(undefined, {
                  minimumFractionDigits: item.decimalPlaces,
                  maximumFractionDigits: item.decimalPlaces,
                })
              : Math.round(n).toLocaleString()

          return (
            <View key={index} className="mb-6">
              <View className="flex-row justify-between items-center mb-3">
                <View className="flex-row items-baseline gap-2">
                  <Text size="title-3" className="text-typography-900">
                    {item.type}
                  </Text>
                  <Text className="text-typography-500">
                    {item.unlimited ? '无限制' : `${fmt(item.totalQuota)} ${item.unit}`}
                  </Text>
                </View>
              </View>

              <View className="mb-3">
                <Progress value={progressPercentage}>
                  <ProgressFilledTrack
                    style={{
                      width: `${progressPercentage}%`,
                    }}
                  />
                </Progress>
              </View>

              <View className="flex-row justify-between">
                <Text className="text-typography-600">
                  已用 {fmt(item.consumedQuota)} {item.unit}
                </Text>
                <Text className="text-typography-600">
                  {item.unlimited ? '无限制' : `剩余 ${fmt(remainingQuota)} ${item.unit}`}
                </Text>
              </View>
            </View>
          )
        })}
      </View>
    </ScrollView>
  )
}
